import {AgGridReact} from 'ag-grid-react';

//Wrapper to show/hide loading overlay while running API requests
export function handleLoading(
    gridRef: React.MutableRefObject<AgGridReact>,
    promise: Promise<void>
): void {
    gridRef.current.api.showLoadingOverlay();
    promise.finally(() => gridRef.current.api.hideOverlay());
}

//Helper to grab the best available error text
export function getErrorText(errorData) {
    if (!errorData) {
        //Probably a 500 error
        return 'The server encountered an error';
    }

    if (errorData.detail) {
        return errorData.detail;
    }

    let message = '';
    for (const error in errorData.errors) {
        message = message + errorData.errors[error] + '\n';
    }

    if (!message && typeof errorData !== 'object') {
        //We've got a simple error message here
        message = errorData;
    }

    return message;
}

//Helper function to map actual booleans used in checkboxes to their corresponding
// pseudo-boolean string values for filtering in a human-readable form
//Doesn't work if declared inline in column definitions, so it lives here instead
export const booleanMap = (params) => {
    return params.data === true ? 'Y' : 'N';
};

//Wrapper to prevent the Enter key being used on an entry field triggering
//Formik's auto-accept functionality
export const skipEnterAccept = (event) => {
    event.key === 'Enter' && event.preventDefault();
};

//For cases where Autocomplete has numeric IDs, sort them that way
// instead of string-style
export const sortAutocompleteIdsNumerically = (a, b) => {
    if (+a.id < +b.id) return -1;
    else if (+a.id > +b.id) return 1;
    else return 0;
};

//Wrapper for the various empty-string checks
export const stringIsEmpty = (str: string) => {
    return str === null || str === undefined || str === ' ' || str === '';
};

//Safely cast to a cash value
export const displayMoney = (value: any) => {
    if (value === null || value === undefined) {
        return '';
    }
    return '$' + value.toFixed(2);
};

//Safely cast a DateTime? to just a date
export const displayDate = (value: any) => {
    if (value === null || value === undefined) {
        return '';
    }
    return value.split('T')[0];
};

//For when we want to compare multiple date-only Date objects
export const stripTimeFromDate = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

//Simple function to make sure the date is in a legal range
//for dropping in to validation checks everywhere
export const isDateValid = (date: Date) => {
    const minDate = new Date('2000-01-01');
    const maxDate = new Date('2099-12-31');

    return date >= minDate && date <= maxDate;
};

//Wrapper function to show a money amount without needing the format class everywhere
export const formatMoney = (amount) => {
    const moneyFormat = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    });

    return moneyFormat.format(amount);
};

//Convert a mask into a more understandable text
export const explainMask = (mask: string) => {
    if (stringIsEmpty(mask)) {
        return '';
    }
    const maskList = mask.split('|');

    //No splits, just the mask itself
    if (maskList.length === 1) {
        return mask;
    }

    let caption = '';
    for (let i = 0; i < maskList.length; i++) {
        if (i > 0) {
            caption += ' or ';
        }
        caption += maskList[i];
    }
    return caption;
};

//Tests a value string against a mask string
//The mask string can contain multiple masks delimited by | characters
export const testStringMask = (value: string, mask: string) => {
    if (stringIsEmpty(mask)) {
        return true;
    }
    const maskList = mask.split('|');

    let pass = false;
    for (let i = 0; i < maskList.length && !pass; i++) {
        const subMask = maskList[i];
        const regEx = convertMaskToRegEx(subMask);
        pass = new RegExp(regEx).test(value);
    }
    return pass;
};

//Convert Reference Information masks to Regular Expressions for validation
//Is hard-coded to prevent a hideous mess of a conversion function that would need messy assumptions to work
const convertMaskToRegEx = (mask: string) => {
    if (mask === '#') {
        return /^[\d\sxX]{1}$/;
    }
    if (mask === '##') {
        return /^[\d\sxX]{1,2}$/;
    }
    if (mask === '#########') {
        return /^[\d\s-]{1,9}$/;
    }
    if (mask === '###########') {
        return /^[\d\s-]{1,11}$/;
    }
    if (mask === '#############') {
        return /^[\d\s-]{1,13}$/;
    }
    if (mask === '###############') {
        return /^[\d\s-]{1,15}$/;
    }
    if (mask === '#######-00') {
        return /^[\d\s-]{1,7}-[\d]{2,2}$/;
    }
    if (mask === 'aaaaaaaaaaaaaaa') {
        return /^[A-Za-z0-9]{1,15}$/;
    }
    if (mask === '999-99-999') {
        return /^[\d\s]{1,3}-[\d\s]{1,2}-[\d\s]{1,3}$/;
    }
    if (mask === '(999) 999-999') {
        return /^\([\d\s]{1,3}\)\s[\d\s]{1,3}-[\d\s]{1,3}$/;
    }
};
